import { useState } from 'react';
import { useLocalization } from '../../contexts/LocalizationContext';
import notificationService from '../../services/notificationService';
import Input from '../../components/Common/Input';
import Button from '../../components/Common/Button';

const PUSH_TYPES = [
  { value: 'announcement', ar: 'إعلان عام (الرئيسية)', en: 'General (open home)' },
  { value: 'home_modal', ar: 'الرئيسية + نافذة لاحقاً', en: 'Home (modal later)' },
  { value: 'product', ar: 'منتج (تفاصيل المنتج)', en: 'Product (open detail)' },
  { value: 'general', ar: 'عام (مثل الإعلان)', en: 'General (same as announcement)' },
];

const AUDIENCES = [
  { value: 'all', ar: 'جميع المستخدمين', en: 'All app users' },
  { value: 'customer', ar: 'عميل محدد (معرّف)', en: 'One customer (ID)' },
];

const NotificationsIndex = () => {
  const { isRtl } = useLocalization();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [pushType, setPushType] = useState('announcement');
  const [productId, setProductId] = useState('');
  const [audience, setAudience] = useState('all');
  const [customerId, setCustomerId] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const t = (ar, en) => (isRtl ? ar : en);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);
    setError(null);
    if (!title.trim() || !body.trim()) {
      setError(t('عنوان ونص الإشعار مطلوبان', 'Title and body are required'));
      return;
    }
    if (pushType === 'product' && !productId.trim()) {
      setError(t('معرّف المنتج مطلوب', 'Product ID is required'));
      return;
    }
    if (audience === 'customer' && !customerId.trim()) {
      setError(t('معرّف العميل مطلوب', 'Customer ID is required'));
      return;
    }
    setLoading(true);
    try {
      const data = await notificationService.send({
        title: title.trim(),
        body: body.trim(),
        pushType,
        productId: pushType === 'product' ? productId.trim() : null,
        audience,
        customerId: audience === 'customer' ? customerId.trim() : null,
      });
      setMessage(
        data?.message ||
          t(`تم الإرسال (${data?.successCount ?? 0} نجاح)`, `Sent (${data?.successCount ?? 0} success)`),
      );
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        t('فشل الإرسال', 'Send failed');
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="notifications-page">
      <div className="page-header">
        <div className="page-header-content">
          <div className="page-header-main">
            <h1 className="page-title">{t('الإشعارات', 'Push Notifications')}</h1>
            <p className="page-description">
              {t(
                'إرسال إشعارات فورية لمستخدمي التطبيق المحمول عبر الخادم',
                'Send push notifications to mobile users through the API',
              )}
            </p>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body" style={{ padding: 'var(--sp-8)' }}>
          <form onSubmit={handleSubmit} className="form-grid" style={{ maxWidth: 560, display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)' }}>
            <Input
              label={t('عنوان الإشعار', 'Notification title')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('مثال: عرض جديد', 'e.g. New offer')}
              required
            />
            <div className="form-group">
              <label className="form-label">{t('نص الإشعار', 'Notification body')} *</label>
              <textarea
                className="form-input"
                rows={4}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder={t('النص الذي يراه المستخدم', 'Message shown to the user')}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">{t('نوع الإشعار', 'Notification type')}</label>
              <select className="form-input" value={pushType} onChange={(e) => setPushType(e.target.value)}>
                {PUSH_TYPES.map((o) => (
                  <option key={o.value} value={o.value}>
                    {isRtl ? o.ar : o.en}
                  </option>
                ))}
              </select>
            </div>

            {pushType === 'product' && (
              <Input
                label={t('معرّف المنتج', 'Product ID')}
                value={productId}
                onChange={(e) => setProductId(e.target.value)}
                placeholder="GUID"
                required
              />
            )}

            <div className="form-group">
              <label className="form-label">{t('المستهدفون', 'Audience')}</label>
              <select className="form-input" value={audience} onChange={(e) => setAudience(e.target.value)}>
                {AUDIENCES.map((o) => (
                  <option key={o.value} value={o.value}>
                    {isRtl ? o.ar : o.en}
                  </option>
                ))}
              </select>
            </div>

            {audience === 'customer' && (
              <Input
                label={t('معرّف العميل (GUID)', 'Customer ID (GUID)')}
                value={customerId}
                onChange={(e) => setCustomerId(e.target.value)}
                placeholder="00000000-0000-0000-0000-000000000000"
                required
              />
            )}

            {error && (
              <div className="error-alert" style={{ margin: 0 }}>
                <span>{error}</span>
              </div>
            )}
            {message && !error && (
              <div className="success-alert" style={{ padding: 'var(--sp-3)', borderRadius: 'var(--r-md)', background: 'rgba(34, 197, 94, 0.12)', color: 'var(--text)' }}>
                {message}
              </div>
            )}

            <div>
              <Button type="submit" variant="primary" disabled={loading}>
                {loading ? t('جاري الإرسال...', 'Sending...') : t('إرسال الإشعار', 'Send notification')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default NotificationsIndex;
